import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AppConfigSnapshot,
  SaveAppConfigArgs,
  deleteAppConfig,
  listAllAppConfigs,
  listAppConfigsForApp,
  saveAppConfig,
} from "@/services/appConfigApi";
import { reconcilePolicyTags } from "@/services/tagAssignmentApi";
import * as tagsApi from "@/services/tagsApi";
import { mapApiTagMinimal, MappedTagMinimal } from "@/utils/tagMapping";
import { getCurrentUserEmail } from "@/lib/auth";
import { AppConfiguration } from "@/types/application";

export const appConfigsQueryKey = (bundleId?: string) => [
  "app-configs",
  bundleId ?? "",
];
export const allAppConfigsQueryKey = ["app-configs", "__all__"] as const;
export const appConfigTagsQueryKey = (policyId?: string) => [
  "app-config-tags",
  policyId ?? "",
];
export const availableTagsQueryKey = ["available-tags"] as const;

// ---------------------------------------------------------------------------
// Legacy stub — kept for /app-configurations standalone page and the legacy
// "Configuration" wizard step in AddApplicationPage / AppConfigurationTab.
// These surfaces were already non-functional ("coming soon") before this
// feature shipped, so preserving them in stub form avoids regressions while
// the real Application Detail flow uses the new bundleId-keyed hooks below.
// ---------------------------------------------------------------------------
export const useAppConfigurations = (applicationId?: string) => {
  const queryClient = useQueryClient();

  const { data: configurations, isLoading } = useQuery({
    queryKey: ["app-configurations-legacy", applicationId],
    queryFn: async (): Promise<AppConfiguration[]> => [],
    enabled: !!applicationId,
  });

  const createConfiguration = async (
    _config: Omit<AppConfiguration, "id" | "createdAt" | "updatedAt">,
  ) => {
    toast.info("App configuration management coming soon");
    return null;
  };

  const updateConfiguration = async (
    _id: string,
    _config: Partial<AppConfiguration>,
  ) => {
    toast.info("App configuration management coming soon");
    return null;
  };

  const deleteConfiguration = async (_id: string) => {
    toast.info("App configuration management coming soon");
  };

  const toggleConfiguration = async (_id: string, _isActive: boolean) => {
    toast.info("App configuration management coming soon");
  };

  // Suppress "queryClient unused" lint without changing behaviour.
  void queryClient;

  return {
    configurations: configurations || [],
    isLoading,
    createConfiguration,
    updateConfiguration,
    deleteConfiguration,
    toggleConfiguration,
  };
};

// ---------------------------------------------------------------------------
// New: real hooks backed by the policies API + naming convention.
// ---------------------------------------------------------------------------

/**
 * List managed app configurations for the given bundleId. Uses the
 * "AppConfig — {bundleId} — …" naming convention to filter polices.
 */
export function useAppConfigsForApp(bundleId?: string) {
  return useQuery({
    queryKey: appConfigsQueryKey(bundleId),
    queryFn: () => listAppConfigsForApp(bundleId!),
    enabled: !!bundleId,
  });
}

/**
 * List managed app configurations across every app — single policies fetch
 * filtered & grouped client-side. Backs the /configurations/app-config page.
 */
export function useAllAppConfigs() {
  return useQuery({
    queryKey: allAppConfigsQueryKey,
    queryFn: () => listAllAppConfigs(),
  });
}

/** Tags currently assigned to a single config policy. */
export function useAppConfigTags(policyId?: string) {
  return useQuery({
    queryKey: appConfigTagsQueryKey(policyId),
    queryFn: async (): Promise<MappedTagMinimal[]> => {
      const tags = await tagsApi.getTagsByPolicy(policyId!);
      return (tags || []).map((t: any) => mapApiTagMinimal(t));
    },
    enabled: !!policyId,
  });
}

/** All tags available in the workspace, for the deployment scope picker. */
export function useAvailableTags() {
  return useQuery({
    queryKey: availableTagsQueryKey,
    queryFn: async (): Promise<MappedTagMinimal[]> => {
      const tags = await tagsApi.listTags();
      return (tags || []).map((t: any) => mapApiTagMinimal(t));
    },
  });
}

interface SaveAppConfigInput extends SaveAppConfigArgs {
  /** Tag ids to assign after policy save. */
  tagIds: string[];
  /** Tag ids previously assigned (used to unassign removed tags in edit mode). */
  previousTagIds?: string[];
}

/**
 * Persist a managed app configuration policy AND its deployment tags.
 *
 * Tag reconciliation: assign newly added tags, unassign removed ones.
 * The policy save is the source of truth — even if a tag mutation throws
 * we keep the policy in place and let the user retry from the dialog.
 */
export function useSaveAppConfig(bundleId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveAppConfigInput) => {
      const { tagIds, previousTagIds, ...saveArgs } = input;
      const result = await saveAppConfig(saveArgs);
      const actor = getCurrentUserEmail() || "system";
      await reconcilePolicyTags({
        policyId: result.id,
        previousTagIds: previousTagIds ?? [],
        nextTagIds: tagIds,
        actor,
      });
      return result;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["app-configs"] });
      qc.invalidateQueries({ queryKey: ["policies"] });
      if (variables.existingPolicyId) {
        qc.invalidateQueries({
          queryKey: appConfigTagsQueryKey(variables.existingPolicyId),
        });
      }
      toast.success("App configuration saved and queued for deployment");
    },
    onError: (err: unknown) => {
      console.error("[useSaveAppConfig] save failed", err);
      const msg =
        err instanceof Error ? err.message : "Failed to save app configuration";
      toast.error(msg);
    },
  });
}

export function useDeleteAppConfig(bundleId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAppConfig(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app-configs"] });
      qc.invalidateQueries({ queryKey: ["policies"] });
      toast.success("App configuration deleted");
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "Failed to delete app configuration";
      toast.error(msg);
    },
  });
}

export type { AppConfigSnapshot };
